import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Bell, Mail, Filter, Save, Clock, MapPin, Building2, Wallet, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Logo } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { SEND_HOURS } from "@/lib/content";

const BUDGETS = [
  { key: "small", label: "Petits marchés (moins de 90 k€)" },
  { key: "medium", label: "Marchés moyens (90 k€ à 500 k€)" },
  { key: "large", label: "Gros marchés (plus de 500 k€)" },
  { key: "any", label: "Tous les montants" },
];

const Section = ({ icon: Icon, title, children }) => (
  <div className="bg-white border border-border rounded-2xl p-6">
    <h3 className="flex items-center gap-2 font-semibold text-slate-900 mb-4"><Icon className="w-4 h-4 text-primary" /> {title}</h3>
    {children}
  </div>
);

export default function SettingsPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ niches: [], departements: {} });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fb, setFb] = useState({ category: "Fonctionnalité", message: "" });
  const [fbSaving, setFbSaving] = useState(false);

  useEffect(() => { api.get("/public/niches").then(({ data }) => setMeta(data)).catch(() => {}); }, []);
  useEffect(() => {
    if (user) {
      setForm({
        company_name: user.profile?.company_name || "",
        description: user.profile?.description || "",
        niche: user.profile?.niche || "communication",
        departements: user.profile?.departements || [],
        location: user.profile?.location || "",
        budget: user.profile?.budget || "any",
        keywords_exclude: user.profile?.keywords_exclude || "",
        alert_email: user.alert_email || user.email || "",
        alerts_enabled: user.alerts_enabled ?? true,
        frequency: user.frequency || "daily",
        send_hour: user.send_hour || 8,
      });
    }
  }, [user]);

  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDep = (c) => set("departements", form.departements.includes(c) ? form.departements.filter((x) => x !== c) : [...form.departements, c]);

  const submitFeedback = async () => {
    if (!fb.message.trim()) return toast.error("Écrivez votre retour.");
    setFbSaving(true);
    try {
      await api.post("/feedback", fb);
      toast.success("Merci, votre retour est transmis à notre équipe.");
      setFb((f) => ({ ...f, message: "" }));
    } catch (e) {
      toast.error("Envoi impossible pour le moment.");
    } finally { setFbSaving(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/profile", form);
      setUser(data);
      if (user?.plan === "essentiel" && form.departements.length > (data.profile?.departements?.length || 0)) {
        toast.info("Formule Essentiel : seuls vos 5 premiers départements sont conservés. Passez en Pro pour couvrir toute la France.");
      }
      toast.success("Vos réglages ont été enregistrés");
    } catch (e) {
      toast.error("Erreur à l'enregistrement");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b border-border">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="settings-back-btn"><ArrowLeft className="w-4 h-4 mr-1.5" /> Mon espace</Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8">
        <h1 className="font-display text-3xl font-extrabold text-slate-900 mb-1">Votre profil et vos alertes</h1>
        <p className="text-slate-500 mb-7">Plus votre profil est précis, meilleure est votre sélection du matin.</p>

        <div className="space-y-5">
          <Section icon={Building2} title="Votre entreprise">
            <label className="text-sm text-slate-600">Nom</label>
            <Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className="mt-1 mb-4" data-testid="settings-company-input" />
            <label className="text-sm text-slate-600">Activité</label>
            <p className="text-xs text-slate-400 mt-0.5">Décrivez votre activité en quelques mots-clés. Cela sert à faire remonter en priorité les marchés qui vous correspondent — ce n'est pas un filtre strict : vous verrez toujours les marchés de votre secteur.</p>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} placeholder="Ex. : audit de cybersécurité, développement web, infogérance de PME…" className="mt-1 mb-4" data-testid="settings-description-input" />
            <label className="text-sm text-slate-600">Secteur</label>
            <div className="grid sm:grid-cols-3 gap-2 mt-1">
              {meta.niches.map((n) => (
                <button key={n.key} onClick={() => set("niche", n.key)} data-testid={`settings-niche-${n.key}`}
                  className={`px-3 py-2 rounded-lg border text-sm text-left transition-all ${form.niche === n.key ? "border-primary bg-blue-50 text-primary font-semibold" : "border-border text-slate-600"}`}>
                  {n.short}
                </button>
              ))}
            </div>
          </Section>

          <Section icon={MapPin} title="Zone géographique">
            <label className="text-sm text-slate-600">Ville / lieu de travail</label>
            <Input value={form.location} onChange={(e) => set("location", e.target.value)} className="mt-1 mb-4" data-testid="settings-location-input" />
            <label className="text-sm text-slate-600">Départements ({form.departements.length === 0 ? "France entière" : form.departements.length})</label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-1 max-h-48 overflow-y-auto pr-1">
              {Object.entries(meta.departements).map(([code, name]) => (
                <button key={code} onClick={() => toggleDep(code)} data-testid={`settings-dep-${code}`}
                  className={`px-2 py-1.5 rounded-lg border text-xs text-left transition-all ${form.departements.includes(code) ? "border-primary bg-blue-50 text-primary font-semibold" : "border-border text-slate-600"}`}>
                  {code} {name}
                </button>
              ))}
            </div>
          </Section>

          <Section icon={Wallet} title="Taille de marché">
            <div className="grid sm:grid-cols-2 gap-2">
              {BUDGETS.map((b) => (
                <button key={b.key} onClick={() => set("budget", b.key)} data-testid={`settings-budget-${b.key}`}
                  className={`px-3 py-2.5 rounded-lg border text-sm text-left transition-all ${form.budget === b.key ? "border-primary bg-blue-50 text-primary font-semibold" : "border-border text-slate-600"}`}>
                  {b.label}
                </button>
              ))}
            </div>
          </Section>

          <Section icon={Bell} title="Fréquence et heure d'envoi">
            <div className="grid grid-cols-2 gap-2 mb-4">
              {[{ k: "daily", l: "Chaque jour" }, { k: "weekly", l: "Chaque lundi" }].map((f) => (
                <button key={f.k} onClick={() => set("frequency", f.k)} data-testid={`settings-freq-${f.k}`}
                  className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-all ${form.frequency === f.k ? "border-primary bg-blue-50 text-primary" : "border-border text-slate-600"}`}>{f.l}</button>
              ))}
            </div>
            <label className="text-sm text-slate-600 flex items-center gap-1.5"><Clock className="w-4 h-4" /> Heure</label>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-1">
              {SEND_HOURS.map((h) => (
                <button key={h.v} onClick={() => set("send_hour", h.v)} data-testid={`settings-hour-${h.v}`}
                  className={`px-2 py-2 rounded-lg border text-sm transition-all ${form.send_hour === h.v ? "border-primary bg-blue-50 text-primary font-semibold" : "border-border text-slate-600"}`}>{h.l}</button>
              ))}
            </div>
            <div className="flex items-center justify-between mt-5 pt-4 border-t border-border">
              <p className="text-sm text-slate-700">Recevoir les alertes email</p>
              <Switch checked={form.alerts_enabled} onCheckedChange={(v) => set("alerts_enabled", v)} data-testid="settings-alerts-toggle" />
            </div>
          </Section>

          <Section icon={Mail} title="Email de réception">
            <Input type="email" value={form.alert_email} onChange={(e) => set("alert_email", e.target.value)} data-testid="settings-email-input" />
          </Section>

          <Section icon={Filter} title="Mots-clés d'exclusion">
            <p className="text-sm text-slate-500 mb-2">Séparez par des virgules. Les marchés contenant ces mots seront écartés.</p>
            <Textarea value={form.keywords_exclude} onChange={(e) => set("keywords_exclude", e.target.value)} rows={2} placeholder="défense, armée…" data-testid="settings-exclude-input" />
          </Section>

          <Button onClick={save} disabled={saving} className="w-full rounded-full h-12" data-testid="settings-save-btn"><Save className="w-4 h-4 mr-1.5" /> {saving ? "Enregistrement…" : "Enregistrer mes réglages"}</Button>

          <Section icon={MessageSquare} title="Vos retours nous aident à améliorer votre accompagnement">
            <p className="text-sm text-slate-500 mb-3">Canal privé, réservé à notre équipe (différent de « Donner mon avis » affiché sur le site). Dites-nous ce qui vous aiderait : fonctionnalités, pertinence, formules…</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {["Fonctionnalité", "Pertinence", "Formule / Prix", "Bug", "Idée", "Autre"].map((c) => (
                <button key={c} onClick={() => setFb((f) => ({ ...f, category: c }))} data-testid={`feedback-cat-${c}`}
                  className={`px-3 py-1.5 rounded-full border text-sm transition-all ${fb.category === c ? "border-primary bg-blue-50 text-primary font-semibold" : "border-border text-slate-600"}`}>{c}</button>
              ))}
            </div>
            <Textarea value={fb.message} onChange={(e) => setFb((f) => ({ ...f, message: e.target.value }))} rows={3} placeholder="Votre retour, votre besoin, une idée d'amélioration…" data-testid="feedback-message-input" />
            <Button onClick={submitFeedback} disabled={fbSaving} variant="outline" className="rounded-full mt-3" data-testid="feedback-submit-btn">{fbSaving ? "Envoi…" : "Envoyer mon retour"}</Button>
          </Section>
        </div>
      </main>
    </div>
  );
}
